import React, { useEffect, useRef, useState } from "react";

interface DeepLinkWrapperProps {
  link: string;
}

type AppLaunchOptions = {
  hostname?: string;
  userAgent?: string;
  maxTouchPoints?: number;
};

export function getAppLaunchUrl(
  link: string,
  {
    hostname = window.location.hostname,
    userAgent = navigator.userAgent,
    maxTouchPoints = navigator.maxTouchPoints,
  }: AppLaunchOptions = {},
): string | null {
  const normalizedLink = link.replace(/^\/+/, "");
  const isStaging =
    hostname === "staging.citymoshi.com" ||
    hostname.endsWith(".staging.citymoshi.com");
  const scheme = isStaging ? "citymoshi-staging" : "citymoshi";
  const packageName = isStaging
    ? "com.citymoshi.app.staging"
    : "com.citymoshi.app";
  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && maxTouchPoints > 1);

  if (isIOS) return `${scheme}://${normalizedLink}`;
  if (/Android/i.test(userAgent)) {
    return `intent://${normalizedLink}#Intent;scheme=${scheme};package=${packageName};end`;
  }
  return null;
}

export function useOpenInApp(link?: string) {
  const attemptedLink = useRef<string>();

  useEffect(() => {
    if (!link || attemptedLink.current === link) return;
    const launchUrl = getAppLaunchUrl(link);
    if (!launchUrl) return;

    attemptedLink.current = link;
    window.location.href = launchUrl;
  }, [link]);
}

const DeepLinkWrapper: React.FC<DeepLinkWrapperProps> = ({ link }) => {
  const [isAppNotInstalled, setIsAppNotInstalled] = useState(false);

  useEffect(() => {
    const launchUrl = getAppLaunchUrl(link);
    if (launchUrl) window.location.href = launchUrl;

    const fallbackTimer = window.setTimeout(() => {
      setIsAppNotInstalled(true);
    }, 3000);
    return () => window.clearTimeout(fallbackTimer);
  }, [link]);

  const handleRedirectIos = () => {
    window.location.href = "https://apps.apple.com/us/app/myapp/id1234567890"; // Replace with your app store link
  };

  const handleRedirectAndroid = () => {
    window.location.href = "https://play.google.com/store/apps/details?id=com.citymoshi.app";
  };

  return (
    <div>
      {!isAppNotInstalled ? (
        <div className="popup"></div>
      ) : (
        <div className="flex flex-col gap-3 items-center">
          <div className="text-center">
            <h1 className="text-md md:text-2xl font-bold capitalize text-BG mb-1">Download CityMoshi to open the link</h1>
          </div>
          <div className="text-center">
            <div className="w-full flex items-center justify-center gap-2.5 md:gap-5 mt-5">
              <button
                className="bg-secondary/60 flex items-center gap-3 cursor-default text-white py-3 px-4 rounded-lg outline-none"
                disabled
                onClick={handleRedirectIos}
              >
                <img src="/images/apple.png" alt="apple" className="w-8 h-8" />
                <div className="flex flex-col justify-center items-start leading-3">
                  <p className="text-white text-xs m-0">Download on the</p>
                  <p className="text-white text-sm md:text-base font-medium m-0 leading-3">
                    App Store
                  </p>
                </div>
              </button>
              <button
                className="bg-secondary/60 flex items-center gap-3 cursor-default text-whit py-3 px-4 rounded-lg outline-none"
                disabled
                onClick={handleRedirectAndroid}
              >
                <img
                  src="/images/playstore.png"
                  alt="google"
                  className="w-8 h-8"
                />
                <div className="flex flex-col justify-center leading-0 items-start">
                  <p className="text-white text-[10px] m-0 p-0 leading-3">
                    Download it on
                  </p>
                  <p className="text-white text-sm md:text-base m-0 p-0 font-medium leading-3">
                    Google Play
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeepLinkWrapper;
